
import { Link } from 'react-router-dom';

const learningPaths = [
  {
    id: '1',
    title: 'Complete Onboarding Guide',
    subtitle: 'Getting Started Series',
    description: 'Everything you need to set up your account, create your first invoice, and start getting paid faster.',
    items: ['Account setup', 'First invoice', 'Client management', 'Payment tracking'],
    duration: '45 min',
    color: 'from-slate-800 to-slate-900',
    icon: 'ri-rocket-line',
    image: 'https://public.readdy.ai/ai/img_res/91b614550241f0eca6fd38ed617b8cc9.jpg'
  },
  {
    id: '2',
    title: 'AI Photo Quoting Mastery',
    subtitle: 'Advanced Features',
    description: 'Master the photo-to-quote feature and learn how to generate accurate estimates in seconds.',
    items: ['Photo tips', 'AI accuracy', 'Custom adjustments', 'Material matching'],
    duration: '30 min',
    color: 'from-teal-600 to-teal-700',
    icon: 'ri-camera-lens-line',
    image: 'https://readdy.ai/api/search-image?query=smartphone%20taking%20photo%20of%20construction%20job%20site%20for%20ai%20analysis%20modern%20technology%20clean%20professional%20style%20warm%20lighting&width=600&height=400&seq=learning-path-2&orientation=landscape'
  },
  {
    id: '3',
    title: 'Business Growth Toolkit',
    subtitle: 'Scale Your Trade',
    description: 'Strategies and templates to grow your trade business, win more clients, and increase profits.',
    items: ['Pricing strategies', 'Client retention', 'Marketing tips', 'Financial planning'],
    duration: '60 min',
    color: 'from-amber-500 to-orange-600',
    icon: 'ri-line-chart-line',
    image: 'https://readdy.ai/api/search-image?query=business%20growth%20chart%20and%20analytics%20dashboard%20showing%20revenue%20increase%20modern%20clean%20design%20warm%20professional%20lighting&width=600&height=400&seq=learning-path-3&orientation=landscape'
  }
];

export default function LearningPaths() {
  return (
    <section className="py-24 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-16">
          <div>
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-slate-200 rounded-full mb-4">
              <i className="ri-road-map-line text-slate-600" />
              <span className="text-slate-700 text-sm font-medium">Structured Learning</span>
            </div>
            <h2 className="text-4xl lg:text-5xl font-bold text-slate-900">
              Featured Learning <span className="text-amber-600">Paths</span>
            </h2>
          </div>
          <p className="text-lg text-slate-600 max-w-md">
            Curated collections of guides and tutorials to help you master specific skills quickly.
          </p>
        </div>

        {/* Learning Paths Grid */}
        <div className="grid lg:grid-cols-3 gap-8">
          {learningPaths.map((path, index) => (
            <div
              key={path.id}
              className={`group relative rounded-3xl overflow-hidden shadow-xl hover:shadow-2xl transition-all duration-500 ${
                index === 0 ? 'lg:row-span-2' : ''
              }`}
            >
              {/* Background Gradient */}
              <div className={`absolute inset-0 bg-gradient-to-br ${path.color}`} />
              
              {/* Background Image */}
              <div className="absolute inset-0 opacity-20">
                <img
                  src={path.image}
                  alt={path.title}
                  className="w-full h-full object-cover"
                />
              </div>

              {/* Content */}
              <div className={`relative p-8 flex flex-col ${index === 0 ? 'h-full min-h-[500px]' : 'min-h-[280px]'}`}>
                {/* Badge */}
                <div className="flex items-center justify-between mb-6">
                  <span className="px-3 py-1.5 bg-white/20 backdrop-blur-sm rounded-full text-white text-sm font-medium">
                    {path.subtitle}
                  </span>
                  <div className="w-10 h-10 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center">
                    <i className={`${path.icon} text-white text-xl`} />
                  </div>
                </div>

                {/* Title & Description */}
                <h3 className={`font-bold text-white mb-3 ${index === 0 ? 'text-3xl' : 'text-2xl'}`}>
                  {path.title}
                </h3>
                <p className={`text-white/80 mb-6 ${index === 0 ? 'text-lg' : 'text-base'}`}>
                  {path.description}
                </p>

                {/* Items List (only for first card) */}
                {index === 0 && (
                  <ul className="space-y-2 mb-8">
                    {path.items.map((item, i) => (
                      <li key={i} className="flex items-center gap-2 text-white/90">
                        <i className="ri-check-line text-amber-400" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                )}

                {/* Footer */}
                <div className="mt-auto flex items-center justify-between">
                  <span className="text-white/70 text-sm">
                    <i className="ri-time-line mr-1" />
                    {path.duration}
                  </span>
                  <Link
                    to="#"
                    className="inline-flex items-center gap-2 px-5 py-2.5 bg-white text-slate-900 rounded-full font-medium hover:bg-slate-100 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    <span>Start Learning</span>
                    <i className="ri-arrow-right-line" />
                  </Link>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
